// src/components/FilterInvoicesModal.js
import React, { useMemo } from "react";
import Select from "react-select";
import { customSelectStyles } from "../styles/reactSelectStyles";
import "../styles/FilterInvoices.css"; // reuse same CSS
import { useTranslation } from "react-i18next";

export default function FilterInvoicesModal({
  open,
  value,
  onChange,
  onApply,
  onClear,
  onClose,
  clients = [], // array de strings
}) {
  const { t } = useTranslation("invoices");

  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });

  // ✅ hooks SEMPRE antes de qualquer return
  const statusOptions = useMemo(
    () => [
      { value: "", label: t("filter.status_options.all") },
      { value: "DRAFT", label: t("status.draft") },
      { value: "SENT", label: t("status.sent") },
      { value: "PARTIAL", label: t("status.partial") },
      { value: "PAID", label: t("status.paid") },
      { value: "VOID", label: t("status.void") },
    ],
    [t]
  );

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c, label: c })),
    [clients]
  );

  const allClientOption = useMemo(
    () => ({ value: "all", label: t("filter.client_options.all") }),
    [t]
  );

  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  // ✅ agora sim: podes sair depois dos hooks
  if (!open) return null;

  return (
    <div className="invoices-filter-modal-backdrop" onClick={onClose}>
      <div
        className="invoices-filter-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("filter.title")}
      >
        <div className="invoices-filter-modal-header">
          <h3>{t("filter.title")}</h3>
          <button
            className="invoices-filter-close"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="invoices-filter-grid">
          {/* Status (react-select) */}
          <div className="invoices-filter-field">
            <label>{t("filter.fields.status")}</label>
            <Select
              classNamePrefix="invoices-select"
              styles={{
                ...customSelectStyles,
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                menu: (b) => ({
                  ...b,
                  zIndex: 9999,
                  border: 0,
                  backgroundColor: "#0f1115",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                  marginTop: 6,
                  borderRadius: 12,
                  overflow: "hidden",
                }),
                menuList: (b) => ({
                  ...b,
                  paddingTop: 0,
                  paddingBottom: 0,
                  maxHeight: 240,
                }),
              }}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              isClearable
              options={statusOptions}
              value={
                statusOptions.find((o) => o.value === (value.status ?? "")) ||
                null
              }
              onChange={(opt) =>
                onChange({
                  ...value,
                  status: opt ? opt.value : "", // limpar = All
                })
              }
              placeholder={t("filter.placeholders.status")}
            />
          </div>

          {/* Client (react-select) */}
          <div className="invoices-filter-field">
            <label>{t("filter.fields.client")}</label>
            <Select
              classNamePrefix="invoices-select"
              styles={{
                ...customSelectStyles,
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                menu: (b) => ({
                  ...b,
                  zIndex: 9999,
                  border: 0,
                  backgroundColor: "#0f1115",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                  marginTop: 6,
                  borderRadius: 12,
                  overflow: "hidden",
                }),
                menuList: (b) => ({
                  ...b,
                  paddingTop: 0,
                  paddingBottom: 0,
                  maxHeight: 240,
                }),
              }}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              isClearable
              options={[allClientOption, ...clientOptions]}
              value={
                clientOptions.find((o) => o.value === value.client) ||
                (value.client === "all" ? allClientOption : null)
              }
              onChange={(opt) =>
                onChange({
                  ...value,
                  client: opt ? opt.value : "all", // fallback = all
                })
              }
              placeholder={t("filter.placeholders.client")}
            />
          </div>

          <div className="invoices-filter-field">
            <label>{t("filter.fields.date_from")}</label>
            <input
              type="date"
              value={value.dateFrom}
              onChange={set("dateFrom")}
            />
          </div>

          <div className="invoices-filter-field">
            <label>{t("filter.fields.date_to")}</label>
            <input type="date" value={value.dateTo} onChange={set("dateTo")} />
          </div>

          <div className="invoices-filter-field">
            <label>{t("filter.fields.pickup")}</label>
            <input
              type="text"
              placeholder={t("filter.placeholders.pickup_example")}
              value={value.pickup}
              onChange={set("pickup")}
            />
          </div>

          <div className="invoices-filter-field">
            <label>{t("filter.fields.delivery")}</label>
            <input
              type="text"
              placeholder={t("filter.placeholders.delivery_example")}
              value={value.delivery}
              onChange={set("delivery")}
            />
          </div>

          <div className="invoices-filter-field">
            <label>{t("filter.fields.min_price")}</label>
            <input
              type="number"
              step="0.01"
              value={value.minPrice}
              onChange={set("minPrice")}
            />
          </div>

          <div className="invoices-filter-field">
            <label>{t("filter.fields.max_price")}</label>
            <input
              type="number"
              step="0.01"
              value={value.maxPrice}
              onChange={set("maxPrice")}
            />
          </div>
        </div>

        <div className="invoices-filter-actions">
          <button className="invoices-btn" onClick={onClear} type="button">
            {t("filter.actions.reset")}
          </button>
          <button className="invoices-btn" onClick={onApply} type="button">
            {t("filter.actions.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
